import React, { useState } from 'react';
import { Download, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { importExportAPI } from '../services/api';

const ImportExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Export all data to Excel
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await importExportAPI.exportAll();

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();

      // Reference sheet (first sheet - important for users to see valid values)
      const referenceWs = XLSX.utils.json_to_sheet(data.reference);
      XLSX.utils.book_append_sheet(wb, referenceWs, 'Reference');

      // Teams sheet
      const teamsWs = XLSX.utils.json_to_sheet(data.teams);
      XLSX.utils.book_append_sheet(wb, teamsWs, 'Teams');

      // Players sheet
      const playersWs = XLSX.utils.json_to_sheet(data.players);
      XLSX.utils.book_append_sheet(wb, playersWs, 'Players');

      // Tournaments sheet
      const tournamentsWs = XLSX.utils.json_to_sheet(data.tournaments);
      XLSX.utils.book_append_sheet(wb, tournamentsWs, 'Tournaments');

      // Matches sheet
      const matchesWs = XLSX.utils.json_to_sheet(data.matches);
      XLSX.utils.book_append_sheet(wb, matchesWs, 'Matches');

      // Generate filename
      const filename = data.is_sample
        ? 'innings_pro_template.xlsx'
        : `innings_pro_data_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);

      toast.success(data.is_sample
        ? 'Template downloaded with 1000 pre-generated IDs! Check the Reference sheet for valid values.'
        : 'Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel file (.xlsx, .xls) or CSV');
      return;
    }

    try {
      const data = await readExcelFile(file);
      setPreviewData(data);
      toast.success('File loaded! Review the data below and click Import to proceed.');
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file');
    }
  };

  // Read Excel file
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'array' });

          const data = {
            teams: [],
            players: [],
            tournaments: [],
            matches: []
          };

          // Read each sheet
          if (workbook.SheetNames.includes('Teams')) {
            data.teams = XLSX.utils.sheet_to_json(workbook.Sheets['Teams']);
          }
          if (workbook.SheetNames.includes('Players')) {
            data.players = XLSX.utils.sheet_to_json(workbook.Sheets['Players']);
          }
          if (workbook.SheetNames.includes('Tournaments')) {
            data.tournaments = XLSX.utils.sheet_to_json(workbook.Sheets['Tournaments']);
          }
          if (workbook.SheetNames.includes('Matches')) {
            data.matches = XLSX.utils.sheet_to_json(workbook.Sheets['Matches']);
          }

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Perform import
  const handleImport = async () => {
    if (!previewData) return;

    setIsImporting(true);
    try {
      const results = await importExportAPI.importAll(previewData);
      setImportResults(results);
      setPreviewData(null);

      const totalSuccess = results.teams.success + results.players.success +
                          results.tournaments.success + results.matches.success;
      const totalFailed = results.teams.failed + results.players.failed +
                         results.tournaments.failed + results.matches.failed;

      if (totalFailed === 0 && totalSuccess > 0) {
        toast.success(`Successfully imported ${totalSuccess} records!`);
      } else if (totalSuccess > 0) {
        toast.success(`Imported ${totalSuccess} records with ${totalFailed} failures`);
      } else if (totalFailed > 0) {
        toast.error(`Import failed with ${totalFailed} errors`);
      } else {
        toast.info('No new records to import');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import data');
    } finally {
      setIsImporting(false);
    }
  };

  // Cancel import
  const handleCancelImport = () => {
    setPreviewData(null);
    setImportResults(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Import / Export Data</h1>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-start space-x-4">
          <div className="bg-green-100 p-3 rounded-lg">
            <Download className="h-8 w-8 text-green-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Export Data</h2>
            <p className="text-gray-600 mb-4">
              Download all your data as an Excel file with separate sheets for Teams, Players, Tournaments, and Matches.
              If you don't have any data yet, you'll get a sample template to fill out.
            </p>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="bg-green-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>Download Excel</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-start space-x-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <Upload className="h-8 w-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Import Data</h2>
            <p className="text-gray-600 mb-4">
              Upload an Excel file to import data. The file should have sheets named: Teams, Players, Tournaments, Matches.
              Download the template first to see the required format.
            </p>

            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <FileSpreadsheet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Drag and drop your Excel file here, or
              </p>
              <label className="cursor-pointer">
                <span className="text-blue-600 hover:text-blue-700 font-medium">browse to upload</span>
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
              </label>
              <p className="text-sm text-gray-500 mt-2">
                Supported formats: .xlsx, .xls, .csv
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {previewData && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Preview Import Data</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-800">{previewData.teams.length}</div>
              <div className="text-gray-600">Teams</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-800">{previewData.players.length}</div>
              <div className="text-gray-600">Players</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-800">{previewData.tournaments.length}</div>
              <div className="text-gray-600">Tournaments</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-800">{previewData.matches.length}</div>
              <div className="text-gray-600">Matches</div>
            </div>
          </div>

          {/* Data Tables */}
          <div className="space-y-4 mb-6">
            {previewData.teams.length > 0 && (
              <details className="border rounded-lg">
                <summary className="p-3 cursor-pointer bg-gray-50 font-medium">
                  Teams ({previewData.teams.length})
                </summary>
                <div className="p-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        {Object.keys(previewData.teams[0] || {}).map(key => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.teams.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.teams.length > 5 && (
                    <p className="text-gray-500 text-sm mt-2">... and {previewData.teams.length - 5} more</p>
                  )}
                </div>
              </details>
            )}

            {previewData.players.length > 0 && (
              <details className="border rounded-lg">
                <summary className="p-3 cursor-pointer bg-gray-50 font-medium">
                  Players ({previewData.players.length})
                </summary>
                <div className="p-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        {Object.keys(previewData.players[0] || {}).map(key => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.players.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.players.length > 5 && (
                    <p className="text-gray-500 text-sm mt-2">... and {previewData.players.length - 5} more</p>
                  )}
                </div>
              </details>
            )}

            {previewData.tournaments.length > 0 && (
              <details className="border rounded-lg">
                <summary className="p-3 cursor-pointer bg-gray-50 font-medium">
                  Tournaments ({previewData.tournaments.length})
                </summary>
                <div className="p-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        {Object.keys(previewData.tournaments[0] || {}).map(key => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.tournaments.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.tournaments.length > 5 && (
                    <p className="text-gray-500 text-sm mt-2">... and {previewData.tournaments.length - 5} more</p>
                  )}
                </div>
              </details>
            )}

            {previewData.matches.length > 0 && (
              <details className="border rounded-lg">
                <summary className="p-3 cursor-pointer bg-gray-50 font-medium">
                  Matches ({previewData.matches.length})
                </summary>
                <div className="p-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        {Object.keys(previewData.matches[0] || {}).map(key => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.matches.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.matches.length > 5 && (
                    <p className="text-gray-500 text-sm mt-2">... and {previewData.matches.length - 5} more</p>
                  )}
                </div>
              </details>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleImport}
              disabled={isImporting}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isImporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Import Data</span>
                </>
              )}
            </button>
            <button
              onClick={handleCancelImport}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Results Section */}
      {importResults && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Import Results</h2>

          <div className="space-y-4">
            {['teams', 'players', 'tournaments', 'matches'].map(entity => (
              <div key={entity} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium capitalize">{entity}</h3>
                  <div className="flex items-center space-x-4">
                    {importResults[entity].success > 0 && (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {importResults[entity].success} success
                      </span>
                    )}
                    {importResults[entity].failed > 0 && (
                      <span className="flex items-center text-red-600">
                        <XCircle className="h-4 w-4 mr-1" />
                        {importResults[entity].failed} failed
                      </span>
                    )}
                  </div>
                </div>
                {importResults[entity].errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 mt-2">
                    <div className="flex items-center text-red-800 mb-2">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      <span className="font-medium">Errors:</span>
                    </div>
                    <ul className="list-disc list-inside text-sm text-red-700">
                      {importResults[entity].errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResults[entity].errors.length > 5 && (
                        <li>... and {importResults[entity].errors.length - 5} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => setImportResults(null)}
            className="mt-4 bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close Results
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-3">How to use:</h3>
        <ol className="list-decimal list-inside text-blue-700 space-y-2">
          <li>Click <strong>Download Excel</strong> to get the template or your existing data</li>
          <li>Fill in or edit the data in Excel (keep the sheet names as they are)</li>
          <li>Upload the file using the drop zone above</li>
          <li>Review the preview and click <strong>Import Data</strong></li>
        </ol>
        <div className="mt-4 text-sm text-blue-600">
          <strong>Note:</strong> Teams are imported first, then Players (which can reference teams by name),
          then Tournaments, and finally Matches (which reference tournaments and teams by name).
        </div>
      </div>
    </div>
  );
};

export default ImportExport;
